// warmCacheOncePerSession.ts

export type WarmCacheOpts = {
  endpoint?: string; // за замовчуванням: офіційний CACHE_ENDPOINT
  perAsset?: boolean; // true = 1 раз/assetId/сесію, false = 1 раз/сесію
  timeoutMs?: number; // таймаут на отримання заголовків відповіді
  keepalive?: boolean; // корисно при pagehide/unload
};

const SESSION_KEY_BASE = "warmCacheTriggered";
const inflight = new Map<string, Promise<boolean>>();

/**
 * Стартує прогрів кешу без читання стріму. Гарантує 1 виклик за сесію
 * (глобально або на кожен assetId). Повертає true на успіху, false на помилці/таймауті.
 */
export async function warmCacheOncePerSession(assetId: string, opts: WarmCacheOpts = {}): Promise<boolean> {
  const {
    endpoint = process.env.REACT_APP_PREVIEW_CACHING_URL as string,
    perAsset = true,
    timeoutMs = 10000,
    keepalive = false,
  } = opts;

  const key = perAsset ? `${SESSION_KEY_BASE}:${assetId}` : SESSION_KEY_BASE;

  const hasSessionStorage = typeof window !== "undefined" && typeof window.sessionStorage !== "undefined";

  // вже робили у цій сесії
  if (hasSessionStorage && sessionStorage.getItem(key)) return false;

  // анти-дубль у межах вкладки
  if (inflight.has(key)) return inflight.get(key)!;

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);

  const req = fetch(endpoint, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ assetId }),
    signal: controller.signal,
    keepalive,
  })
    .then(async (res) => {
      // не читаємо стрім
      try {
        res.body?.cancel();
      } catch {}

      if (!res.ok) throw new Error(`Warm cache failed: ${res.status} ${res.statusText}`);

      if (hasSessionStorage) {
        sessionStorage.setItem(key, new Date().toISOString());
      }
      return true;
    })
    .catch((err) => {
      if ((err as any)?.name === "AbortError") {
        console.warn("warmCache: aborted by timeout");
      } else {
        console.warn("warmCache: error", err);
      }
      return false;
    })
    .finally(() => {
      clearTimeout(timer);
      inflight.delete(key);
    });

  inflight.set(key, req);
  return req;
}
